import type { ReactNode } from "react";
import { Icon, type IconName } from "./Icon";

/* ── EmptyState ─────────────────────────────────────────────────────────
 * Centered icon + specific copy + optional action. Per FD voice, lead empty
 * states with a fact, not hype ("No active contracts." not "Get started!").
 */
export interface EmptyStateProps {
  icon?: IconName;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}

export function EmptyState({ icon = "inbox", title, description, action, compact }: EmptyStateProps) {
  return (
    <div className={["fd-empty", compact && "fd-empty--compact"].filter(Boolean).join(" ")}>
      <span className="fd-empty__icon">
        <Icon name={icon} size={compact ? 22 : 28} />
      </span>
      <div className="fd-empty__title">{title}</div>
      {description && <p className="fd-empty__desc">{description}</p>}
      {action && <div className="fd-empty__action">{action}</div>}
    </div>
  );
}

/* ── Skeleton ───────────────────────────────────────────────────────────── */
export function Skeleton({
  width,
  height = 16,
  radius,
  className,
}: {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  className?: string;
}) {
  return (
    <span
      className={["fd-skeleton", className].filter(Boolean).join(" ")}
      style={{ width, height, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

/** A few stacked skeleton lines for loading text/blocks. */
export function SkeletonText({ lines = 3 }: { lines?: number }) {
  return (
    <div className="fd-skeleton-text" aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} height={12} width={i === lines - 1 ? "60%" : "100%"} />
      ))}
    </div>
  );
}

/* ── Spinner ────────────────────────────────────────────────────────────── */
export function Spinner({ size = 18 }: { size?: number }) {
  return <span className="fd-spinner" style={{ width: size, height: size }} aria-label="Loading" role="status" />;
}

/* ── Banner / inline alert ──────────────────────────────────────────────── */
export type BannerTone = "info" | "success" | "warning" | "danger";

const bannerIcon: Record<BannerTone, IconName> = {
  info: "info",
  success: "checkCircle",
  warning: "alert",
  danger: "alert",
};

export interface BannerProps {
  tone?: BannerTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}

export function Banner({ tone = "info", title, children, action }: BannerProps) {
  return (
    <div className={`fd-banner fd-banner--${tone}`} role={tone === "danger" ? "alert" : "status"}>
      <Icon name={bannerIcon[tone]} size={18} className="fd-banner__icon" />
      <div className="fd-banner__content">
        {title && <div className="fd-banner__title">{title}</div>}
        {children && <div className="fd-banner__body">{children}</div>}
      </div>
      {action && <div className="fd-banner__action">{action}</div>}
    </div>
  );
}
